// src/sections/notifications/notificationTypes.js
// ─────────────────────────────────────────────────────────────────
// All 16 notification card type definitions.
// Used by NotificationCard to resolve icon, colour tier, category
// label, and default action buttons per notification type.
//
// Backend-provided fields (do NOT duplicate here):
//   priority       — 'urgent' | 'normal' | 'low'  (drives card accent / pulse)
//   action_label   — single CTA string per card (overrides `actions` array below)
//   requires_action — boolean, drives Action / Updates tab split
//   grouped_count  — number, shows grouped count bar when > 1
//   snoozable      — boolean, shows snooze button when true
//
// The `actions` arrays below are FALLBACK only — used when the
// backend does not provide `action_label` for a notification.
// ─────────────────────────────────────────────────────────────────

// ── Colour palette tokens (Minimals) ────────────────────────────
export const NOTIFICATION_PALETTE = {
  error: {
    lighter: '#FFE9D5',
    main: '#FF5630',
    dark: '#B71D18',
  },
  warning: {
    lighter: '#FFF5CC',
    main: '#FFAB00',
    dark: '#B76E00',
  },
  info: {
    lighter: '#CAFDF5',
    main: '#00B8D9',
    dark: '#006C9C',
  },
  success: {
    lighter: '#D3FCD2',
    main: '#22C55E',
    dark: '#118D57',
  },
  primary: {
    lighter: '#C8FAD6',
    main: '#00A76F',
    dark: '#007867',
  },
  grey: {
    100: '#F9FAFB',
    200: '#F4F6F8',
    300: '#DFE3E8',
    400: '#C4CDD5',
    500: '#919EAB',
    600: '#637381',
  },
  whatsapp: {
    lighter: '#E8F5E9',
    main: '#25D366',
    dark: '#128C7E',
  },
  google: {
    lighter: '#FFF8E1',
    main: '#F4B400',
    dark: '#E8A000',
  },
  facebook: {
    lighter: '#EBF3FF',
    main: '#1877F2',
  },
};

// ── Tier → accent colour mapping ────────────────────────────────
export const TIER_ACCENT = {
  urgent: NOTIFICATION_PALETTE.error.main,
  warning: NOTIFICATION_PALETTE.warning.main,
  leads: NOTIFICATION_PALETTE.info.main,
  whatsapp: NOTIFICATION_PALETTE.whatsapp.main,
  positive: NOTIFICATION_PALETTE.success.main,
  external: NOTIFICATION_PALETTE.grey[400],
};

// ── Icon colour presets ─────────────────────────────────────────
const ICON = {
  red: { bg: NOTIFICATION_PALETTE.error.lighter, color: NOTIFICATION_PALETTE.error.dark },
  amber: { bg: NOTIFICATION_PALETTE.warning.lighter, color: NOTIFICATION_PALETTE.warning.dark },
  blue: { bg: NOTIFICATION_PALETTE.info.lighter, color: NOTIFICATION_PALETTE.info.dark },
  green: { bg: NOTIFICATION_PALETTE.success.lighter, color: NOTIFICATION_PALETTE.success.dark },
  teal: { bg: NOTIFICATION_PALETTE.primary.lighter, color: NOTIFICATION_PALETTE.primary.dark },
  grey: { bg: NOTIFICATION_PALETTE.grey[200], color: NOTIFICATION_PALETTE.grey[600] },
  wa: { bg: NOTIFICATION_PALETTE.whatsapp.lighter, color: NOTIFICATION_PALETTE.whatsapp.main },
  google: { bg: NOTIFICATION_PALETTE.google.lighter, color: NOTIFICATION_PALETTE.google.main },
};

// ── Chip presets ────────────────────────────────────────────────
export const CHIP_PRESETS = {
  facebook: { bg: '#EBF3FF', color: '#1877F2', icon: 'facebook' },
  web: { bg: NOTIFICATION_PALETTE.info.lighter, color: NOTIFICATION_PALETTE.info.dark, icon: 'language' },
  treatment: { bg: NOTIFICATION_PALETTE.primary.lighter, color: NOTIFICATION_PALETTE.primary.dark, icon: 'medical_services' },
  call: { bg: '#E3F2FD', color: '#1976D2', icon: 'phone' },
  overdue: { bg: NOTIFICATION_PALETTE.error.lighter, color: NOTIFICATION_PALETTE.error.main, icon: 'schedule' },
  google: { bg: '#FFF8E1', color: '#E8A000', icon: 'location_on' },
  whatsapp: { bg: '#E8F5E9', color: '#128C7E', icon: 'chat' },
  email: { bg: '#FFF3E0', color: '#E65100', icon: 'mail' },
};

// ── Action button presets ───────────────────────────────────────
// variant: 'primary' | 'urgent' | 'whatsapp' | 'default'
const ACT = {
  callBack: { label: 'Call Back', icon: 'phone', variant: 'urgent' },
  callNow: { label: 'Call Now', icon: 'phone', variant: 'urgent' },
  assign: { label: 'Assign', icon: 'person_add', variant: 'primary' },
  openTask: { label: 'Open Task', icon: 'open_in_new', variant: 'primary' },
  openLead: { label: 'Open Lead', icon: 'open_in_new', variant: 'primary' },
  claimContact: { label: 'Claim & Contact', icon: 'person_add', variant: 'primary' },
  openThread: { label: 'Open Thread', icon: 'open_in_new', variant: 'primary' },
  reply: { label: 'Reply', icon: 'reply', variant: 'whatsapp' },
  viewFacebook: { label: 'View on Facebook', icon: 'open_in_new', variant: 'primary' },
  replyGoogle: { label: 'Reply on Google', icon: 'reply', variant: 'primary' },
  analytics: { label: 'Analytics', icon: 'open_in_new', variant: 'primary' },
  viewLead: { label: 'View Lead', icon: 'open_in_new', variant: 'primary' },
  view: { label: 'View', icon: 'open_in_new', variant: 'default' },
  task: { label: 'Task', icon: 'open_in_new', variant: 'default' },
  dismiss: { label: 'Dismiss', icon: 'close', variant: 'default' },
};

// ── 16 Notification type definitions ────────────────────────────
const NOTIFICATION_TYPES = {
  // ── 🔴 URGENT ──
  CALLBACK_REQUEST: {
    key: 'CALLBACK_REQUEST',
    tier: 'urgent',
    category: 'Callback Request',
    icon: 'phone_callback',
    iconColor: ICON.red,
    actions: [ACT.callBack, ACT.view, ACT.dismiss],
  },
  TASK_OVERDUE: {
    key: 'TASK_OVERDUE',
    tier: 'urgent',
    category: 'Overdue',
    icon: 'warning_amber',
    iconColor: ICON.red,
    actions: [ACT.callNow, ACT.task, ACT.dismiss],
  },

  // ── 🟡 WARNING ──
  LEAD_UNASSIGNED: {
    key: 'LEAD_UNASSIGNED',
    tier: 'warning',
    category: 'Unassigned',
    icon: 'person_off',
    iconColor: ICON.amber,
    actions: [ACT.assign, ACT.view, ACT.dismiss],
  },
  TASK_DUE_TODAY: {
    key: 'TASK_DUE_TODAY',
    tier: 'warning',
    category: 'Due Today',
    icon: 'schedule',
    iconColor: ICON.amber,
    actions: [ACT.openTask, ACT.dismiss],
  },
  DEPOSIT_NOT_PAID: {
    key: 'DEPOSIT_NOT_PAID',
    tier: 'warning',
    category: 'Deposit Overdue',
    icon: 'payment',
    iconColor: ICON.amber,
    actions: [ACT.openLead, ACT.dismiss],
  },

  // ── 🔵 LEADS & COMMS ──
  NEW_LEAD: {
    key: 'NEW_LEAD',
    tier: 'leads',
    category: 'New Lead',
    icon: 'person_add',
    iconColor: ICON.blue,
    actions: [ACT.claimContact, ACT.view, ACT.dismiss],
  },
  LEAD_ASSIGNED: {
    key: 'LEAD_ASSIGNED',
    tier: 'leads',
    category: 'Lead Assigned',
    icon: 'assignment_ind',
    iconColor: ICON.blue,
    actions: [ACT.openLead, ACT.dismiss],
  },
  TASK_ASSIGNED: {
    key: 'TASK_ASSIGNED',
    tier: 'leads',
    category: 'Task Assigned',
    icon: 'task',
    iconColor: ICON.blue,
    actions: [ACT.openTask, ACT.dismiss],
  },
  EMAIL_REPLY: {
    key: 'EMAIL_REPLY',
    tier: 'leads',
    category: 'Email Reply',
    icon: 'mail',
    iconColor: ICON.blue,
    hasPreview: true,
    actions: [ACT.openThread, ACT.dismiss],
  },
  FACEBOOK_COMMENT: {
    key: 'FACEBOOK_COMMENT',
    tier: 'leads',
    category: 'Facebook Comment',
    icon: 'comment',
    iconColor: ICON.blue,
    hasPreview: true,
    actions: [ACT.viewFacebook, ACT.dismiss],
  },
  LEAD_RESTORED: {
    key: 'LEAD_RESTORED',
    tier: 'leads',
    category: 'Lead Restored',
    icon: 'restore',
    iconColor: ICON.blue,
    actions: [ACT.openLead, ACT.dismiss],
  },

  // ── 💚 WHATSAPP ──
  WHATSAPP_MESSAGE: {
    key: 'WHATSAPP_MESSAGE',
    tier: 'whatsapp',
    category: 'WhatsApp Message',
    icon: 'chat',
    iconColor: ICON.wa,
    hasPreview: true,
    actions: [ACT.reply, ACT.viewLead, ACT.dismiss],
  },

  // ── 🟢 POSITIVE ──
  DEPOSIT_PAID: {
    key: 'DEPOSIT_PAID',
    tier: 'positive',
    category: 'Payment',
    icon: 'payments',
    iconColor: ICON.green,
    actions: [ACT.viewLead, ACT.dismiss],
  },
  TREATMENT_STARTED: {
    key: 'TREATMENT_STARTED',
    tier: 'positive',
    category: 'Treatment',
    icon: 'medical_services',
    iconColor: ICON.teal,
    actions: [ACT.viewLead, ACT.dismiss],
  },

  // ── ⚪ EXTERNAL ──
  GOOGLE_REVIEW: {
    key: 'GOOGLE_REVIEW',
    tier: 'external',
    category: 'Google Review',
    icon: 'star',
    iconColor: ICON.google,
    hasPreview: true,
    hasStarRating: true,
    actions: [ACT.replyGoogle, ACT.dismiss],
  },
  PHONE_CLICKED: {
    key: 'PHONE_CLICKED',
    tier: 'external',
    category: 'Website',
    icon: 'phone_in_talk',
    iconColor: ICON.grey,
    actions: [ACT.analytics, ACT.dismiss],
  },

  // ── 🔵 FOLLOW-UP ──
  LEAD_FOLLOW_UP_MOVED: {
    key: 'LEAD_FOLLOW_UP_MOVED',
    tier: 'leads',
    category: 'Follow Up',
    icon: 'trending_up',
    iconColor: ICON.blue,
    actions: [ACT.openLead, ACT.dismiss],
  },
};

export default NOTIFICATION_TYPES;

/**
 * Helper: get type config by key string
 * @param {string} typeKey — e.g. 'NEW_LEAD', 'WHATSAPP_MESSAGE'
 * @returns {object} notification type config
 */
export function getNotificationType(typeKey) {
  return NOTIFICATION_TYPES[typeKey] || NOTIFICATION_TYPES.NEW_LEAD;
}
